import React, { useMemo } from 'react';
import MemberCell from '../components/MemberCell';
import EditableFieldCell from '../components/EditableFieldCell';
import DateCell from '../components/DateCell';
import StatusSelect from '../components/StatusSelect';
import Tooltip from '@/components/Tooltip';

const isExpiredTime = (time) => {
  let datetime = new Date(time);
  datetime.setMinutes(datetime.getMinutes() + 90);
  return new Date() > datetime;
};

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'bg-success-light text-success';
    case 'suspended':
      return 'bg-error-light text-error';
    case 'inactive':
      return 'bg-gray-100 text-inactive-text';
    default:
      return 'bg-gray-100 text-inactive-text';
  }
};

const getWarningStatusColor = (status) => {
  switch (status) {
    case 'FINAL':
      return 'bg-light-secondary-yellow border-warning-final-light';
    case 'SUSPENDED':
      return 'bg-error-light border-warning-suspend-light';
    case 'INITIAL':
      return 'bg-secondary-yellow-light border-warning-initial-light';
    case 'NORMAL':
      return 'bg-success-light border-warning-green-light';
    default:
      return '';
  }
};

export const useMembersTableHeaders = ({
  loginLoading,
  handleLoginUser,
  handleChangeStatus,
  handleFieldClick,
  renderTableActions,
  handleOpenWarningsModal,
  warningsLoadingMemberId,
}) => {
  return useMemo(
    () => [
      {
        label: '#',
        field: 'id',
        flex: 0.3,
        component: (row) => row.id,
      },
      {
        label: 'Member',
        field: 'member',
        flex: 1.8,
        textAlign: 'left',
        component: (row) => (
          <MemberCell
            member={row}
            onLogin={handleLoginUser}
            loginLoading={loginLoading}
          />
        ),
      },
      {
        label: 'Email Address',
        field: 'email',
        flex: 0.8,
        component: (row) => (
          <Tooltip
            tooltipId={`email-address-${row.id}`}
            iconId={`email-address-icon-${row.id}`}
            interactive
            title={row.email}
            placement="top"
            arrow>
            <span className="whitespace-nowrap max-w-128 line-clamp-1">
              {row.email}
            </span>
          </Tooltip>
        ),
      },
      {
        label: 'Company',
        field: 'company',
        flex: 0.8,
        component: (row) => (
          <Tooltip
            className="w-full"
            tooltipId={`company-${row.id}`}
            iconId={`company-icon-${row.id}`}
            interactive
            title={row.company}
            placement="top"
            arrow>
            <span className="whitespace-nowrap flex justify-center w-full line-clamp-1">
              {row.company || '-'}
            </span>
          </Tooltip>
        ),
      },
      {
        label: 'Status',
        field: 'status',
        flex: 0.6,
        component: (row) => {
          const value = row?.expire_warnings_type_text || '-';
          const isLoading = warningsLoadingMemberId === row.id;

          return (
            <EditableFieldCell
              value={value}
              statusColor={getWarningStatusColor(row?.expire_warnings_type)}
              displayValue={value}
              onClick={() =>
                value !== '-' ? handleOpenWarningsModal(row) : null
              }
              isLoading={isLoading}
            />
          );
        },
      },
      {
        label: 'Integration',
        field: 'integration',
        flex: 0.6,
        component: (row) => {
          if (row.active_excel_mode === 1) return 'TMS/FTP';
          if (row.active_excel_mode === 2) return 'TMS/Email';
          if (row.active_excel_mode === 3) return 'Excel And GoogleSheet';
          if (row.active_excel_mode === 4) return 'Email';
          if (row.active_excel_mode === 5) return 'Application';
          return '-';
        },
      },
      {
        label: 'Naming',
        field: 'naming',
        flex: 0.6,
        component: (row) => {
          if (row.active_excel_mode === 1 || row.active_excel_mode === 2) {
            return (
              <span className="whitespace-pre-wrap">
                {row.tms?.name || '-'}
              </span>
            );
          }
          return (
            <span className="whitespace-pre-wrap">
              Manual / {row.tms_manual_alias || '-'}
            </span>
          );
        },
      },
      {
        label: (
          <>
            <span>
              Last <br />
              Update
            </span>
          </>
        ),
        field: 'last_update',
        flex: 0.5,
        component: (row) => {
          const expired =
            row.last_inventory_update &&
            isExpiredTime(row.last_inventory_update.updated_at);
          if (row.last_inventory_update) {
            return (
              <DateCell
                date={row.last_inventory_update.updated_at}
                isExpired={expired}
              />
            );
          }
          return '';
        },
      },
      {
        label: (
          <>
            <span>
              Last <br />
              Import
            </span>
          </>
        ),
        field: 'last_import',
        flex: 0.5,
        component: (row) => {
          const expired =
            row.metas?.last_excel_import &&
            isExpiredTime(row.metas?.last_excel_import?.value);
          if (row?.metas?.last_excel_import?.value) {
            return (
              <DateCell
                date={row.metas.last_excel_import.value}
                isExpired={expired}
              />
            );
          }
          return '';
        },
      },
      {
        label: 'Plan',
        field: 'plan',
        flex: 0.6,
        component: (row) => {
          const value = row.stripe?.sub_price || '';
          const displayValue = value ? `$${value}` : '-';
          return (
            <EditableFieldCell
              value={value}
              displayValue={displayValue}
              onClick={() => handleFieldClick(row, 'plan')}
            />
          );
        },
      },
      {
        label: 'Trial Days',
        field: 'trial_days',
        flex: 0.6,
        component: (row) => {
          const value = row.stripe?.trial_duration || '';
          const displayValue = value ? `${value} days` : '-';
          return (
            <EditableFieldCell
              value={value}
              displayValue={displayValue}
              onClick={() => handleFieldClick(row, 'trial_days')}
            />
          );
        },
      },
      {
        label: (
          <>
            <span>
              Trial <br />
              Start Date
            </span>
          </>
        ),
        field: 'trial_start_date',
        flex: 0.6,
        component: (row) => {
          const trialStartDate = row.stripe?.trial_strat_at;
          return <DateCell date={trialStartDate} />;
        },
      },
      {
        label: (
          <>
            <span>
              Trial <br />
              End Date
            </span>
          </>
        ),
        field: 'trial_end_date',
        flex: 0.6,
        component: (row) => {
          const trialEndDate = row.stripe?.trial_ends_at;
          return <DateCell date={trialEndDate} />;
        },
      },
      {
        label: (
          <>
            <span>
              Last Payment <br />
              Amount
            </span>
          </>
        ),
        field: 'last_payment_amount',
        flex: 0.6,
        component: (row) => row.stripe?.last_payment_amount || '-',
      },
      {
        label: (
          <>
            <span>
              Last Amount <br />
              Streetturn
            </span>
          </>
        ),
        field: 'last_amount_streetturn',
        flex: 0.6,
        component: (row) => row.stripe?.last_metered_amount || '-',
      },
      {
        label: (
          <>
            <span>
              Last <br /> Payment
            </span>
          </>
        ),
        field: 'last_payment',
        flex: 0.6,
        component: (row) => {
          const lastPayment = row.stripe?.last_payment_date;
          if (lastPayment && lastPayment !== '-') {
            return <DateCell date={lastPayment} />;
          }
          return '-';
        },
      },
      {
        label: (
          <>
            <span>
              Next <br />
              Payment
            </span>
          </>
        ),
        field: 'next_payment',
        flex: 0.6,
        component: (row) => {
          const nextPayment = row.stripe?.next_payment_date;
          if (nextPayment && nextPayment !== '-') {
            return <DateCell date={nextPayment} />;
          }
          return '-';
        },
      },
      {
        label: 'Credit',
        field: 'credit',
        flex: 0.6,
        component: (row) => {
          const value = row.stripe?.credit;
          const displayValue = value ? `$${value}` : '-';
          return (
            <EditableFieldCell
              value={value}
              displayValue={displayValue}
              onClick={() => handleFieldClick(row, 'credit')}
            />
          );
        },
      },
      {
        label: (
          <>
            <span>
              Outstanding <br />
              Balance
            </span>
          </>
        ),
        field: 'outstanding_balance',
        flex: 0.6,
        component: (row) => row.stripe?.outstanding_amount || '-',
      },
      {
        label: (
          <>
            <span>
              Payment <br />
              Status
            </span>
          </>
        ),
        field: 'payment_status',
        flex: 0.6,
        component: (row) =>
          row.stripe?.status ? (
            <span
              className={`rounded-full capitalize px-3 h-7 flex items-center justify-center text-xs font-medium ${getStatusColor(
                row.stripe?.status
              )}`}>
              {row.stripe?.status}
            </span>
          ) : (
            '-'
          ),
      },
      {
        label: 'Status',
        field: 'status',
        flex: 0.8,
        component: (row) => (
          <StatusSelect
            value={row.status}
            onChange={(status) => handleChangeStatus(row.id, status)}
          />
        ),
      },
      {
        label: 'Action',
        field: 'action',
        flex: 0.8,
        component: (row) => renderTableActions(row),
      },
    ],
    [
      loginLoading,
      handleChangeStatus,
      handleLoginUser,
      renderTableActions,
      handleFieldClick,
      handleOpenWarningsModal,
      warningsLoadingMemberId,
    ]
  );
};
